import type { PersonRequest } from "@plinknz/tah-website-elements";

import { equalish } from "../boolean-functions";
import { getPhone, PhoneType } from "../phone";

import type { AddressMeta } from "../../components/form/address-finder-input";
import type { SVGSourceData, SVGProcessData } from "./svg-config";
import type { SVGLine } from "./svg";

interface GetPersonalChangesProps {
    member: PersonRequest;
    phone: string;
    email: string;
    addressfinder: AddressMeta;
    default?: PersonRequest;
    applicant?: string;
    name?: string;
}

export function getPersonalChanges(
    data: GetPersonalChangesProps
): SVGProcessData {
    const { member, phone, email, addressfinder } = data;

    const svgLines: SVGLine[] = [{ heading: "Contact Changes" }];
    const existingCell = getPhone({
        phone: member.phone,
        type: PhoneType.Cell,
    });
    const existingPhone = getPhone({
        phone: member.phone,
        type: PhoneType.Home,
    });

    if (!equalish(phone, existingCell) && !equalish(phone, existingPhone)) {
        svgLines.push({ label: "Phone", text: phone });
    }

    if (!equalish(email, member.email[0]?.value)) {
        svgLines.push({ label: "Email", text: email });
    }

    if (addressfinder) {
        const existing = member.address[0];

        if (
            (!existing && addressfinder.address_line_1) ||
            !equalish(existing.line1, addressfinder.address_line_1) ||
            !equalish(existing.line2, addressfinder.address_line_2) ||
            !equalish(existing.city, addressfinder.city) ||
            !equalish(existing.postcode, addressfinder.postcode)
        ) {
            const newAddress: string[] = [];
            [
                addressfinder.address_line_1,
                addressfinder.address_line_2,
                addressfinder.suburb,
                `${addressfinder.city}${
                    addressfinder.postcode ? ` ${addressfinder.postcode}` : ""
                }`,
                addressfinder.country,
            ].forEach((line) => {
                if (line) {
                    newAddress.push(line);
                }
            });
            svgLines.push({
                label: "Address",
                text: newAddress.join(", "),
            });
        }
    }

    const {
        default: _default,
        member: _member,
        phone: _phone,
        email: _email,
        addressfinder: _addressFinder,
        name: _name,
        ...rest
    } = data;

    const remainingData = (rest as unknown) as SVGSourceData;
    if (rest.applicant) {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        delete (remainingData as any).applicant;
    }

    if (svgLines.length === 1) {
        return {
            svgLines: ([] as unknown) as SVGLine[],
            remainingData,
        };
    }

    return { svgLines, remainingData };
}
